import { buildPaginationParams, type PaginationState, type RequiredPaginationParams, type ResponseMeta } from "@investhinks/api-client";
import { useCallback, useState } from "react";

export type { PaginationState, ResponseMeta };
export type PaginationParams = RequiredPaginationParams;

export interface UseStrapiPaginationReturn {
  pagination: PaginationState;
  setPagination: (pagination: Partial<PaginationState> | ((prev: PaginationState) => Partial<PaginationState>)) => void;
  goToPage: (page: number) => void;
  changePageSize: (pageSize: number) => void;
  resetPagination: () => void;
  updateFromResponse: (meta: ResponseMeta | undefined) => void;
  paginationParams: PaginationParams;
}

const DEFAULT_PAGINATION: PaginationState = {
  page: 1,
  pageSize: 10,
  pageCount: 1,
  total: 0,
};

/**
 * Hook for managing Strapi pagination state
 */
const useStrapiPagination = (initialPagination: Partial<PaginationState> = {}): UseStrapiPaginationReturn => {
  const [pagination, setPaginationState] = useState<PaginationState>({
    ...DEFAULT_PAGINATION,
    ...initialPagination,
  });

  const setPagination = useCallback(
    (newPagination: Partial<PaginationState> | ((prev: PaginationState) => Partial<PaginationState>)) => {
      setPaginationState((prev) => ({
        ...prev,
        ...(typeof newPagination === "function" ? newPagination(prev) : newPagination),
      }));
    },
    []
  );

  const goToPage = useCallback((page: number) => {
    setPaginationState((prev) => ({ ...prev, page }));
  }, []);

  const changePageSize = useCallback((pageSize: number) => {
    setPaginationState((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const resetPagination = useCallback(() => {
    setPaginationState((prev) => ({
      ...DEFAULT_PAGINATION,
      pageSize: prev.pageSize, // Keep user's preferred page size
    }));
  }, []);

  const updateFromResponse = useCallback((meta: ResponseMeta | undefined) => {
    if (meta?.pagination) {
      setPaginationState((prev) => ({
        ...prev,
        ...meta.pagination,
      }));
    }
  }, []);

  // Format for API request
  const paginationParams = buildPaginationParams(pagination);

  return {
    pagination,
    setPagination,
    goToPage,
    changePageSize,
    resetPagination,
    updateFromResponse,
    paginationParams,
  };
};

export default useStrapiPagination;
